import React, { useState } from "react"
import Icon from "../Icon"
import { formatDateLong } from "../../utils/format"

function GroupInvitesPanel({ invites, onAccept, onReject }) {
  const hasInvites = Array.isArray(invites) && invites.length > 0
  const [localStatus, setLocalStatus] = useState({}) // { [inviteId]: 'accepted' | 'rejected' }

  const handleAccept = (id) => {
    setLocalStatus(prev => ({ ...prev, [id]: 'accepted' }))
    setTimeout(() => onAccept?.(id), 500) // Delay slightly for visual feedback
  }

  const handleReject = (id) => {
    setLocalStatus(prev => ({ ...prev, [id]: 'rejected' }))
    setTimeout(() => onReject?.(id), 500)
  }

  return (
    <div className="bg-white rounded-3xl border border-gray-100 shadow-xl shadow-gray-100/50 p-6 overflow-hidden relative">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 bg-gradient-to-br from-sky-100 to-blue-50 rounded-xl flex items-center justify-center shadow-inner">
          <Icon name="bell" size="1.2rem" className="text-sky-600" />
        </div>
        <div>
          <h2 className="text-lg font-bold text-gray-900">
            Group Invitations
          </h2>
          <p className="text-xs text-gray-500 font-medium">
            Manage your pending requests
          </p>
        </div>
      </div>

      {!hasInvites && (
        <div className="border-2 border-dashed border-gray-100 rounded-2xl py-12 text-center bg-gray-50/50">
          <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center mx-auto mb-4 shadow-sm border border-gray-100">
            <Icon name="envelope-open" size="1.5rem" className="text-gray-300" />
          </div>
          <p className="text-sm text-gray-900 font-semibold mb-1">No pending invitations</p>
          <p className="text-xs text-gray-500 px-8">
            When you're invited to a group, the request will appear here.
          </p>
        </div>
      )}

      {hasInvites && (
        <div className="space-y-4">
          {invites.map((invite) => {
            const status = localStatus[invite.id] || invite.status;
            const isPending = status === 'pending';

            return (
              <div
                key={invite.id}
                className={`relative overflow-hidden transition-all duration-300 border rounded-2xl p-5 ${isPending
                    ? "bg-white border-gray-200 shadow-sm hover:shadow-md hover:border-sky-100"
                    : "bg-gray-50 border-gray-100 opacity-75 grayscale-[0.5]"
                  }`}
              >
                {/* Status Overlay for local feedback */}
                {localStatus[invite.id] && (
                  <div className="absolute inset-0 z-10 flex items-center justify-center bg-white/80 backdrop-blur-[1px] transition-opacity">
                    <div className={`flex items-center gap-2 px-4 py-2 rounded-full font-bold shadow-sm ${localStatus[invite.id] === 'accepted' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                      }`}>
                      <Icon name={localStatus[invite.id] === 'accepted' ? 'check' : 'x'} size="1rem" />
                      {localStatus[invite.id] === 'accepted' ? 'Accepted' : 'Declined'}
                    </div>
                  </div>
                )}

                <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
                  <div className="flex-1 min-w-0 space-y-1">
                    <div className="flex items-center gap-2">
                      <h3 className="text-base font-bold text-gray-900 truncate">
                        {invite.groupName}
                      </h3>
                      {!isPending && (
                        <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full ${status === 'accepted' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                          }`}>
                          {status}
                        </span>
                      )}
                    </div>

                    <div className="flex items-center gap-2 text-xs text-gray-500">
                      <div className="flex items-center gap-1 bg-gray-100 px-2 py-0.5 rounded-md">
                        <Icon name="user" size="0.75rem" />
                        <span className="font-medium text-gray-700">{invite.inviterName}</span>
                      </div>
                      <span>•</span>
                      <span>{invite.createdAt ? formatDateLong(invite.createdAt) : "Recently"}</span>
                    </div>

                    {!!invite.message && (
                      <div className="mt-2 text-xs text-gray-600 bg-gray-50 p-2.5 rounded-xl border border-gray-100 italic">
                        "{invite.message}"
                      </div>
                    )}
                  </div>

                  {isPending && (
                    <div className="flex items-center gap-2 w-full sm:w-auto mt-2 sm:mt-0">
                      <button
                        type="button"
                        onClick={() => handleAccept(invite.id)}
                        className="flex-1 sm:flex-none flex items-center justify-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold bg-sky-500 text-white hover:bg-sky-600 active:scale-95 transition-all shadow-md shadow-sky-200 hover:shadow-lg hover:shadow-sky-300/50"
                      >
                        <Icon name="check" size="1rem" />
                        Accept
                      </button>
                      <button
                        type="button"
                        onClick={() => handleReject(invite.id)}
                        className="flex-1 sm:flex-none flex items-center justify-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold bg-white border border-gray-200 text-gray-600 hover:bg-red-50 hover:text-red-600 hover:border-red-200 active:scale-95 transition-all"
                      >
                        Decline
                      </button>
                    </div>
                  )}
                </div>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}

export default GroupInvitesPanel
